import React, { useState, useEffect } from 'react';
import { InputNumber, Button, Table } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { LoadingOutlined } from '@ant-design/icons';
import newAlert from 'src/components/newAlert';
import { start_loading, end_loading } from 'src/core/actions/loader';
import { roundFloat, replaceFloat } from 'src/core/utils/formater'; // Import necessary formatter
import debounce from 'lodash.debounce';

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editingValue, setEditingValue] = useState(null);

  // Debounce handler for the value change to avoid immediate rounding
  const debouncedSave = debounce((value) => {
    const roundedValue = roundFloat(value); // Round after debounce
    handleSave(record.key, dataIndex, roundedValue);
  }, 300); // Adjust debounce delay (300ms here)

  const handleChange = (value) => {
    setEditingValue(value); // Temporarily hold the raw input before rounding
    debouncedSave(value);
  };

  useEffect(
    () =>
      // Clean up debounce on unmount
      () => {
        debouncedSave.cancel();
      },
    []
  );

  let childNode = children || '';

  if (editable && record) {
    childNode = (
      <InputNumber
        min={0}
        value={editingValue !== null ? editingValue : record[dataIndex]}
        onChange={handleChange}
        className="wide-input-number" // Apply the custom class
        step={0.01} // Allow smooth input for decimal numbers
        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
      />
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const App = () => {
  const initialData = [
    { key: '1', title: '1. ХӨРӨНГӨ' },
    {
      key: '1.1',
      category: '1.1 Эргэлтийн хөрөнгө',
      eh_uld: 0.0,
      ets_uld: 0.0,
      editable: false,
      lvl2: true,
    },
    {
      key: '1.1.1',
      category: '1.1.1 Мөнгө,түүнтэй адилтгах хөрөнгө',
      eh_uld: 0.0,
      ets_uld: 0.0,
      editable: true,
      sub: true,
    },
    {
      key: '1.1.2',
      category: '1.1.2 Дансны авлага',
      eh_uld: 0.0,
      ets_uld: 0.0,
      editable: true,
      sub: true,
    },
    // Add additional rows here...
  ];

  const [dataSource, setDataSource] = useState(initialData);
  const { socket, data_date } = useSelector((state) => ({
    socket: state.socket,
    data_date: state.data_date,
  }));
  const dispatch = useDispatch();

  const handleSave = (key, dataIndex, value) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    const item = newData[index];

    // Round the value before saving it
    const roundedValue = replaceFloat(value);
    console.log('Original value:', value);
    console.log('Rounded value:', roundedValue);
    // Update the data source with the rounded value
    newData.splice(index, 1, { ...item, [dataIndex]: roundedValue });

    setDataSource(newData);
  };
  const handleSend = async () => {
    socket.on('request_out', ({ status, content }) => {
      socket.removeAllListeners('request_out');
      newAlert({
        type: status === '0' ? 'success' : 'error',
        msg: content.Message,
        functions: { onSuccess: get_data(data_date.month) },
      });
      dispatch(end_loading({}));
    });

    const content = {
      month: data_date?.month,
      action_type: 'save_orlogiin_delgerengui',
      table_data: dataSource,
    };

    socket.emit('request_in', {
      channel: 'ts001_05_report',
      content,
    });

    dispatch(start_loading({}));
  };

  const get_data = (month) => {
    socket.on('request_out', ({ status, content }) => {
      console.log('Data received:', content);
      socket.removeAllListeners('request_out');
      if (status === '0') {
        setDataSource(content?.result_list?.tailan_data);
      } else {
        newAlert({ torol: 'error', msg: 'Системд алдаа гарлаа' });
      }
      dispatch(end_loading({}));
    });

    socket.emit('request_in', {
      channel: 'ts001_05_view',
      content: { action_type: 'orlogiin_delgerengui', month },
    });

    dispatch(start_loading({}));
  };

  useEffect(() => {
    get_data(data_date.month);
  }, [data_date.month]);

  // Number Formatter
  const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Define table columns
  const columns = [
    {
      title: 'Үзүүлэлт',
      dataIndex: 'category',
      key: 'category',
      width: 600,
      align: 'left',
      render: (text, record) => {
        if (record.title) {
          return {
            children: <strong>{record.title}</strong>,
            props: { colSpan: 9 },
          };
        }
        return (
          <span className={record.lvl2 ? 'lvl2-class' : 'sub-class'}>
            {text}
          </span>
        );
      },
    },
    {
      title: 'Эхний үлдэгдэл',
      dataIndex: 'eh_uld',
      key: 'eh_uld',
      editable: true,
      width: 200,
      render: (text, record) => {
        if (record.title) return { children: null, props: { colSpan: 0 } };
        return typeof text === 'number' ? numberFormatter.format(text) : text;
      },
    },
    {
      title: 'Эцсийн үлдэгдэл /төгрөгөөр/',
      dataIndex: 'ets_uld',
      key: 'ets_uld',
      editable: true,
      width: 200,
      render: (text, record) => {
        if (record.title) return { children: null, props: { colSpan: 0 } };
        return typeof text === 'number' ? numberFormatter.format(text) : text;
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: record.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div className="tax-account-table-antd-div tailan-container">
      <Table
        dataSource={dataSource}
        columns={mergedColumns}
        rowClassName="editable-row"
        bordered
        pagination={false}
        components={{ body: { cell: EditableCell } }}
        footer={() => (
          <Button
            type="primary"
            className="tax-account-button-unique-mini"
            onClick={handleSend}
          >
            Хадгалах
          </Button>
        )}
      />
    </div>
  );
};

export default App;
